import { BookingStateEnum } from '@xeetr/types/enums'
import {
  BaseBookingModel,
  FoodBookingModel,
  ServiceBookingModel,
  ViewCustomer,
} from '@xeetr/types/models'
import { isFoodBookingModel, isServiceBookingModel } from '@xeetr/types/utils'
import { ArrowLeft, Building, Mail, Phone } from 'lucide-react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import { useAbsences } from '../../hooks/useAbsences'
import { useColumnVisibility } from '../../hooks/dashboard/useColumnVisibility'
import { useCustomerBookings } from '../../hooks/useCustomerBookings'
import { useDashboardColumns } from '../../hooks/dashboard/useDashboardColumns'
import { useRowStyle } from '../../hooks/dashboard/useRowStyle'
import { useEvents } from '../../hooks/useEvents'
import { useSidebar } from '../../hooks/useSidebar'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import EmptyState from '@/components/ui/empty-state'
import { useColumnSizing } from '@/hooks/useUserSettings'
import { cn } from '@/lib/utils'
import { SidebarType } from '@/types/Sidebar'
import { parseAbsenceBookingId, parseEventBookingId } from '@/utils/bookingIds'
import { useColumnOrder } from '@/hooks/useColumnOrder'
import { useCustomer } from '@/hooks'


const CustomerBookings = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { setSidebar } = useSidebar()
  const columns = useDashboardColumns()
  const stateCustomer = (location.state as { customer?: ViewCustomer })?.customer
  const { customer, isLoading: customerLoading } = useCustomer(customerId ?? '', stateCustomer)

  const { bookings, isLoading: bookingsLoading } = useCustomerBookings(customerId)
  const { absences } = useAbsences()
  const { events } = useEvents()

  // Column sizing persistence
  const { columnSizing, setColumnSizing } = useColumnSizing('customer-bookings')

  const { handleRowStyle } = useRowStyle()
  const { initialColumnVisibility, handleColumnVisibilityChange } = useColumnVisibility(
    columns,
    'dashboard'
  )
  const { orderedColumns, updateColumnOrder } = useColumnOrder('dashboard')

  const handleBackToCustomers = () => {
    const searchParams = (location.state as { searchParams?: string })?.searchParams
    const backUrl = searchParams ? `/customers?${searchParams}` : '/customers'
    navigate(backUrl)
  }

  // Show error state if customer is not found after loading completes
  // wait for both the customer query and bookings query to finish
  if (!customer && !bookingsLoading && !customerLoading) {
    return (
      <div className="p-6">
        <div className="py-12 text-center">
          <p className="text-muted-foreground">Kunde nicht gefunden.</p>
          <Button onClick={handleBackToCustomers} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zu Kunden
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBackToCustomers} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zu Kunden
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="mb-2 text-2xl font-bold">
              {customer ? (
                <>
                  Buchungen für {customer.firstName} {customer.lastName}
                </>
              ) : (
                <span className="animate-pulse">Laden...</span>
              )}
            </h1>
            {customer?.companyName && (
              <div className="text-muted-foreground mb-2 flex items-center gap-1">
                <Building className="h-4 w-4" />
                <span>{customer.companyName}</span>
              </div>
            )}
            {customer && (
              <div className="text-muted-foreground flex gap-4 text-sm">
                {customer.mail && (
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <a href={`mailto:${customer.mail}`} className="hover:underline">
                      {customer.mail}
                    </a>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    <a href={`tel:${customer.phone}`} className="hover:underline">
                      {customer.phone}
                    </a>
                  </div>
                )}
                {customer.info?.address &&
                  (customer.info.address.street ||
                    customer.info.address.postalCode ||
                    customer.info.address.city) && (
                    <div className="flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      <span>
                        {customer.info.address.street && `${customer.info.address.street}, `}
                        {customer.info.address.postalCode && `${customer.info.address.postalCode} `}
                        {customer.info.address.city}
                      </span>
                    </div>
                  )}
              </div>
            )}
          </div>
          <Badge variant="secondary" className="text-sm">
            {bookings.length} Buchungen
          </Badge>
        </div>
      </div>

      {/* Bookings Table */}
      <DataTable
        columns={columns}
        columnOrder={orderedColumns}
        data={bookings}
        showColumnVisibility={true}
        showPagination={false}
        isFetching={bookingsLoading}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        initialColumnVisibility={initialColumnVisibility}
        enableColumnResizing={true}
        initialColumnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        enableColumnReordering={true}
        alternateRowColors={true}
        onColumnOrderChange={updateColumnOrder}
        className="flex flex-col"
        tableWrapperClassName="flex-1 overflow-hidden"
        tableClassName="h-full"
        emptyState={
          <EmptyState
            title="Keine Buchungen gefunden"
            description="Dieser Kunde hat noch keine Buchungen."
          />
        }
        rowClassName={(booking) =>
          cn(handleRowStyle(booking), 'hover:bg-muted/50', {
            'cursor-pointer': booking.state?.status !== BookingStateEnum.HOLIDAY,
          })
        }
        onRowClick={(row: BaseBookingModel) => {
          const bookingState = row.state?.status
          if (
            (isFoodBookingModel(row) || isServiceBookingModel(row)) &&
            bookingState !== BookingStateEnum.ABSENCE &&
            bookingState !== BookingStateEnum.EVENT
          ) {
            setSidebar({
              type: SidebarType.BOOKING_EDIT,
              open: true,
              booking: row as FoodBookingModel | ServiceBookingModel,
            })
          } else if (bookingState === BookingStateEnum.ABSENCE) {
            const parsed = parseAbsenceBookingId(row.id)
            if (parsed) {
              const absence = absences.find((a) => a.id === parsed.absenceId)
              if (absence) {
                setSidebar({
                  type: SidebarType.ABSENCE_EDIT,
                  open: true,
                  absence: absence,
                })
              }
            }
          } else if (bookingState === BookingStateEnum.EVENT) {
            const parsed = parseEventBookingId(row.id)
            if (parsed) {
              const event = events.find((e) => e.id === parsed.eventId)
              if (event) {
                setSidebar({
                  type: SidebarType.EVENT_EDIT,
                  open: true,
                  event: event,
                })
              }
            }
          }
        }}
      />
    </div>
  )
}

export default CustomerBookings
